import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { MonthYearSelector } from '@travellegacy/nexops-design-system';
import { ConfirmDialog } from '@travellegacy/nexops-design-system';
import { financialSnapshotAPI, api } from '@/shared/lib/api';
import { formatCurrency } from '@/shared/lib/utils';
import { useAuth } from '@/shared/contexts/AuthContext';
import {
  Loader2,
  DollarSign,
  TrendingUp,
  CreditCard,
  Users,
  PiggyBank,
  Receipt,
  Briefcase,
  Wrench,
  PartyPopper,
  Building2,
  Headphones,
  Megaphone,
  Lock,
  Unlock,
  AlertCircle,
} from 'lucide-react';

interface FinancialData {
  month: number;
  year: number;
  totalGrossRevenue: number;
  totalNetRevenue: number;
  totalFees: number;
  totalSales: number;
  agencyProfit: number;
  agencyFlightsCount: number;
  agencyFlightMilesCount: number;
  agencyAccommodationsCount: number;
  agencyInsuranceCount: number;
  agencyOtherServicesCount: number;
  totalCommissions: number;
  totalEmployeeCost: number;
  totalProfitAdvances: number;
  totalOperationalCosts: number;
  taxAmount: number;
  isManualTax?: boolean; // true: imposto vem das despesas da categoria Impostos e Encargos
  totalAgreementDiscounts: number;
  operationalCostsTools: number;
  operationalCostsEvents: number;
  operationalCostsOffice: number;
  operationalCostsServices: number;
  operationalCostsMarketing: number;
  operationalCostsRefunds: number;
  totalCost: number;
  netProfit: number;
  profitAfterAdvances: number;
  profitAdvancesEnded?: boolean; // Mês depois do corte do caixa: sem antecipações
  isFrozenByCutoff?: boolean; // Mês até o corte do caixa: não pode ser reaberto
  isSnapshot: boolean;
  snapshotId?: string;
  closedAt?: string;
  closedBy?: {
    id: string;
    name: string;
    email: string;
  };
}

export default function FinancialDashboard() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [closeMonthDialogOpen, setCloseMonthDialogOpen] = useState(false);
  const [reopenMonthDialogOpen, setReopenMonthDialogOpen] = useState(false);

  // Query única para dados financeiros (usa snapshot se fechado, calcula se aberto)
  const { data: financialDataResponse, isLoading } = useQuery({
    queryKey: ['financial-data', selectedMonth, selectedYear],
    queryFn: async () => {
      const response = await financialSnapshotAPI.getFinancialData(selectedMonth, selectedYear);
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Depois do corte do caixa os sócios não fazem mais antecipação de lucro
  const profitAdvancesEnded = !!financialDataResponse?.data?.profitAdvancesEnded;

  // Query para buscar antecipações do mês (para validar se pode fechar; só antes do corte do caixa)
  const { data: advancesResponse } = useQuery({
    queryKey: ['profit-advances', selectedMonth, selectedYear],
    queryFn: async () => {
      const response = await api.get(`/billing/profit-advances/month/${selectedMonth}/${selectedYear}`);
      return response.data;
    },
    enabled: !!financialDataResponse && !profitAdvancesEnded,
  });

  // Mutation para fechar mês
  const closeMonthMutation = useMutation({
    mutationFn: () => financialSnapshotAPI.closeMonth(selectedMonth, selectedYear),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-data', selectedMonth, selectedYear] });
    },
  });

  // Mutation para reabrir mês
  const reopenMonthMutation = useMutation({
    mutationFn: () => financialSnapshotAPI.reopenMonth(selectedMonth, selectedYear),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-data', selectedMonth, selectedYear] });
    },
  });

  const data: FinancialData = financialDataResponse?.data || {
    month: selectedMonth,
    year: selectedYear,
    totalGrossRevenue: 0,
    totalNetRevenue: 0,
    totalFees: 0,
    totalSales: 0,
    agencyProfit: 0,
    agencyFlightsCount: 0,
    agencyFlightMilesCount: 0,
    agencyAccommodationsCount: 0,
    agencyInsuranceCount: 0,
    agencyOtherServicesCount: 0,
    totalCommissions: 0,
    totalEmployeeCost: 0,
    totalProfitAdvances: 0,
    totalOperationalCosts: 0,
    taxAmount: 0,
    totalAgreementDiscounts: 0,
    operationalCostsTools: 0,
    operationalCostsEvents: 0,
    operationalCostsOffice: 0,
    operationalCostsServices: 0,
    operationalCostsMarketing: 0,
    operationalCostsRefunds: 0,
    totalCost: 0,
    netProfit: 0,
    profitAfterAdvances: 0,
    isSnapshot: false,
  };

  const advances = advancesResponse?.data || [];
  // Antes do corte, fechar o mês exige as antecipações registradas; depois, não há antecipações
  const hasAdvances = profitAdvancesEnded || advances.length > 0;

  const isAdmin = user?.role === 'ADMIN';
  const isClosed = data.isSnapshot && data.closedAt;

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  // Verificar se é o mês atual
  const isCurrentMonth = selectedMonth === now.getMonth() + 1 && selectedYear === now.getFullYear();

  // Verificar se faltam 3 dias ou menos para o fim do mês
  const lastDayOfMonth = new Date(selectedYear, selectedMonth, 0).getDate();
  const currentDay = now.getDate();
  const daysUntilEndOfMonth = lastDayOfMonth - currentDay;
  const isNearEndOfMonth = daysUntilEndOfMonth <= 3;

  // Lógica de visibilidade do card de status e ações:
  // - Mês atual: mostrar apenas se faltarem 3 dias ou menos
  // - Meses anteriores: sempre mostrar (para permitir fechar se não fechado, ou reabrir se fechado)
  const shouldShowStatusCard =
    (isCurrentMonth && isNearEndOfMonth) || // Mês atual nos últimos 3 dias
    !isCurrentMonth;                         // Qualquer mês anterior

  const handleCloseMonth = () => {
    setCloseMonthDialogOpen(true);
  };

  const handleReopenMonth = () => {
    setReopenMonthDialogOpen(true);
  };

  const handleConfirmCloseMonth = () => {
    closeMonthMutation.mutate();
    setCloseMonthDialogOpen(false);
  };

  const handleConfirmReopenMonth = () => {
    reopenMonthMutation.mutate();
    setReopenMonthDialogOpen(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold">Dashboard Financeiro</h1>
          <p className="text-muted-foreground">
            Visão geral das métricas financeiras da agência
          </p>
        </div>

        <MonthYearSelector
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          onMonthChange={setSelectedMonth}
          onYearChange={setSelectedYear}
          disableFutureMonths={true}
        />
      </div>

      {/* Status e Ações */}
      {shouldShowStatusCard && (
        <Card>
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* Badge de Status */}
                {isClosed ? (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 dark:bg-green-900/20 border border-green-300 dark:border-green-700 rounded-lg">
                    <Lock className="h-4 w-4 text-green-700 dark:text-green-400" />
                    <span className="text-sm font-medium text-green-700 dark:text-green-400">
                      Mês Fechado
                    </span>
                  </div>
                ) : data.isSnapshot ? (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-100 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700 rounded-lg">
                    <Unlock className="h-4 w-4 text-blue-700 dark:text-blue-400" />
                    <span className="text-sm font-medium text-blue-700 dark:text-blue-400">
                      Snapshot Salvo (não fechado)
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg">
                    <TrendingUp className="h-4 w-4 text-gray-700 dark:text-gray-300" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Dados em Tempo Real
                    </span>
                  </div>
                )}

                {/* Info de fechamento */}
                {data.closedAt && data.closedBy && (
                  <span className="text-xs text-muted-foreground">
                    Fechado por {data.closedBy.name} em {new Date(data.closedAt).toLocaleDateString('pt-BR')}
                  </span>
                )}
              </div>

              {/* Botões de Ação (apenas ADMIN) */}
              {isAdmin && (
                <div className="flex gap-2">
                  {isClosed && data.isFrozenByCutoff ? (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-muted border rounded-lg">
                      <Lock className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        Congelado pelo corte do caixa
                      </span>
                    </div>
                  ) : isClosed ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleReopenMonth}
                      disabled={reopenMonthMutation.isPending}
                    >
                      {reopenMonthMutation.isPending ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Reabrindo...
                        </>
                      ) : (
                        <>
                          <Unlock className="h-4 w-4 mr-2" />
                          Reabrir Mês
                        </>
                      )}
                    </Button>
                  ) : !hasAdvances ? (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-100 dark:bg-orange-900/20 border border-orange-300 dark:border-orange-700 rounded-lg">
                      <AlertCircle className="h-4 w-4 text-orange-700 dark:text-orange-400" />
                      <span className="text-sm font-medium text-orange-700 dark:text-orange-400">
                        Registre as antecipações de lucro antes de fechar o mês
                      </span>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      onClick={handleCloseMonth}
                      disabled={closeMonthMutation.isPending}
                    >
                      {closeMonthMutation.isPending ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Fechando...
                        </>
                      ) : (
                        <>
                          <Lock className="h-4 w-4 mr-2" />
                          Fechar Mês
                        </>
                      )}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* SEÇÃO SUPERIOR - Cards Principais */}
        <div className={`grid grid-cols-1 gap-6 ${profitAdvancesEnded ? 'md:grid-cols-3' : 'md:grid-cols-2 xl:grid-cols-4'}`}>
          {/* Receita Total: Receita - Despesa = Lucro */}
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/30 dark:to-blue-900/20 border-blue-200 dark:border-blue-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                    Receita Total
                  </p>
                  <p className="text-3xl font-bold text-blue-600 dark:text-blue-400 mt-2">
                    {formatCurrency(data.totalNetRevenue + data.agencyProfit)}
                  </p>
                  <p className="text-xs text-blue-600/70 dark:text-blue-400/70 mt-2">
                    {formatCurrency(data.totalNetRevenue)} serviço + {formatCurrency(data.agencyProfit)} agência
                  </p>
                </div>
                <div className="p-4 bg-blue-200 dark:bg-blue-900/40 rounded-lg">
                  <DollarSign className="h-8 w-8 text-blue-700 dark:text-blue-300" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Custo Total */}
          <Card className="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-950/30 dark:to-red-900/20 border-red-200 dark:border-red-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-800 dark:text-red-300">
                    Despesa Total
                  </p>
                  <p className="text-3xl font-bold text-red-600 dark:text-red-400 mt-2">
                    {formatCurrency(data.totalCost)}
                  </p>
                  <p className="text-xs text-red-600/70 dark:text-red-400/70 mt-2">
                    Comissões + Colaboradores + Custos + Imposto
                  </p>
                </div>
                <div className="p-4 bg-red-200 dark:bg-red-900/40 rounded-lg">
                  <Receipt className="h-8 w-8 text-red-700 dark:text-red-300" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Lucro Líquido */}
          <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950/30 dark:to-green-900/20 border-green-200 dark:border-green-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-800 dark:text-green-300">
                    Lucro Líquido
                  </p>
                  <p className="text-3xl font-bold text-green-600 dark:text-green-400 mt-2">
                    {formatCurrency(data.netProfit)}
                  </p>
                  <p className="text-xs text-green-600/70 dark:text-green-400/70 mt-2">
                    (Lucro Agência + Vendas) - Custo Total
                  </p>
                </div>
                <div className="p-4 bg-green-200 dark:bg-green-900/40 rounded-lg">
                  <TrendingUp className="h-8 w-8 text-green-700 dark:text-green-300" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Caixa (lucro - antecipações): só até o corte do caixa; depois seria igual ao Lucro Líquido */}
          {!profitAdvancesEnded && (
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/30 dark:to-blue-900/20 border-blue-200 dark:border-blue-800">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                      Caixa
                    </p>
                    <p className="text-3xl font-bold text-blue-600 dark:text-blue-400 mt-2">
                      {formatCurrency(data.profitAfterAdvances)}
                    </p>
                    <p className="text-xs text-blue-600/70 dark:text-blue-400/70 mt-2">
                      Lucro Líquido - Antecipações ({formatCurrency(data.totalProfitAdvances)})
                    </p>
                  </div>
                  <div className="p-4 bg-blue-200 dark:bg-blue-900/40 rounded-lg">
                    <PiggyBank className="h-8 w-8 text-blue-700 dark:text-blue-300" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* SEÇÃO INFERIOR - Cards Detalhados */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Total em Taxas */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Total em Taxas
                  </p>
                  <p className="text-2xl font-bold text-orange-600 dark:text-orange-400 mt-1">
                    {formatCurrency(data.totalFees)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Taxas do Mercado Pago
                  </p>
                </div>
                <div className="p-3 bg-orange-100 dark:bg-orange-900/20 rounded-lg">
                  <CreditCard className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Valor de Comissões */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Valor de Comissões
                  </p>
                  <p className="text-2xl font-bold text-teal-600 dark:text-teal-400 mt-1">
                    {formatCurrency(data.totalCommissions)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Comissões dos colaboradores
                  </p>
                </div>
                <div className="p-3 bg-teal-100 dark:bg-teal-900/20 rounded-lg">
                  <Users className="h-6 w-6 text-teal-600 dark:text-teal-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Custo com Colaboradores */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Custo com Colaboradores
                  </p>
                  {/*
                    `totalEmployeeCost` já vem do backend com os descontos de
                    acordos subtraídos (employeeService.getTotalEmployeeCost).
                    Subtrair de novo aqui descontava o mesmo valor duas vezes.

                    O prejuízo assumido pelo colaborador ENTRA neste número
                    justamente como `totalAgreementDiscounts`: é a parcela do
                    acordo devida no mês — apurada pela data da parcela, não
                    pela data de registro do incidente.
                  */}
                  <p className="text-2xl font-bold text-amber-600 dark:text-amber-400 mt-1">
                    {formatCurrency(data.totalEmployeeCost)}
                  </p>
                  {data.totalAgreementDiscounts > 0 ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {formatCurrency(data.totalEmployeeCost + data.totalAgreementDiscounts)}
                      {` - ${formatCurrency(data.totalAgreementDiscounts)} acordos/prejuízo`}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Salário + encargos + benefícios (sem comissões)
                    </p>
                  )}
                </div>
                <div className="p-3 bg-amber-100 dark:bg-amber-900/20 rounded-lg">
                  <Briefcase className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Antecipação de Lucro (só até o corte do caixa) */}
          {!profitAdvancesEnded && (
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Antecipação de Lucro
                    </p>
                    <p className="text-2xl font-bold text-purple-600 dark:text-purple-400 mt-1">
                      {formatCurrency(data.totalProfitAdvances)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Antecipação baseada na % dos sócios
                    </p>
                  </div>
                  <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                    <PiggyBank className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Custo - Ferramentas */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Ferramentas
                  </p>
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">
                    {formatCurrency(data.operationalCostsTools)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Assinaturas e softwares
                  </p>
                </div>
                <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                  <Wrench className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Custo - Eventos */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Eventos
                  </p>
                  <p className="text-2xl font-bold text-pink-600 dark:text-pink-400 mt-1">
                    {formatCurrency(data.operationalCostsEvents)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Eventos e confraternizações
                  </p>
                </div>
                <div className="p-3 bg-pink-100 dark:bg-pink-900/20 rounded-lg">
                  <PartyPopper className="h-6 w-6 text-pink-600 dark:text-pink-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Custo - Escritório */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Escritório
                  </p>
                  <p className="text-2xl font-bold text-slate-600 dark:text-slate-400 mt-1">
                    {formatCurrency(data.operationalCostsOffice)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Aluguel, móveis e equipamentos
                  </p>
                </div>
                <div className="p-3 bg-slate-100 dark:bg-slate-900/20 rounded-lg">
                  <Building2 className="h-6 w-6 text-slate-600 dark:text-slate-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Custo - Serviços */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Serviços
                  </p>
                  <p className="text-2xl font-bold text-cyan-600 dark:text-cyan-400 mt-1">
                    {formatCurrency(data.operationalCostsServices)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Serviços contratados
                  </p>
                </div>
                <div className="p-3 bg-cyan-100 dark:bg-cyan-900/20 rounded-lg">
                  <Headphones className="h-6 w-6 text-cyan-600 dark:text-cyan-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Custo - Marketing */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Marketing
                  </p>
                  <p className="text-2xl font-bold text-orange-600 dark:text-orange-400 mt-1">
                    {formatCurrency(data.operationalCostsMarketing)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Anúncios e divulgação
                  </p>
                </div>
                <div className="p-3 bg-orange-100 dark:bg-orange-900/20 rounded-lg">
                  <Megaphone className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Prejuízos e Reembolsos */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Prejuízos e Reembolsos
                  </p>
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400 mt-1">
                    {formatCurrency(data.operationalCostsRefunds)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Incidentes e reembolsos
                  </p>
                </div>
                <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                  <AlertCircle className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Imposto */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Imposto
                  </p>
                  <p className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1">
                    {formatCurrency(data.taxAmount)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {data.isManualTax ? 'Lançado em Despesas (Impostos e Encargos)' : '8% sobre mês anterior'}
                  </p>
                </div>
                <div className="p-3 bg-red-100 dark:bg-red-900/20 rounded-lg">
                  <Receipt className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

      {/* Dialogs de Confirmação */}
      <ConfirmDialog
        open={closeMonthDialogOpen}
        onOpenChange={setCloseMonthDialogOpen}
        title={`Fechar Mês (${monthNames[selectedMonth - 1]}/${selectedYear})`}
        description={
          <div className="space-y-4">
            <p>
              Ao fechar, os dados financeiros deste mês serão <strong>congelados</strong> e não
              serão mais calculados em tempo real.
            </p>

            <div>
              <p className="font-semibold mb-2">Valores que serão salvos:</p>
              <ul className="space-y-1 ml-4">
                <li>• Despesa Total: <strong>{formatCurrency(data.totalCost)}</strong></li>
                <li>• Lucro Líquido: <strong>{formatCurrency(data.netProfit)}</strong></li>
                <li>• Custo Colaboradores: <strong>{formatCurrency(data.totalEmployeeCost)}</strong></li>
                {!profitAdvancesEnded && (
                  <li>• Antecipações: <strong>{formatCurrency(data.totalProfitAdvances)}</strong></li>
                )}
              </ul>
            </div>

            <p className="text-sm text-muted-foreground">
              Esta ação pode ser revertida posteriormente através do botão "Reabrir Mês".
            </p>
          </div>
        }
        confirmText="Fechar Mês"
        cancelText="Cancelar"
        onConfirm={handleConfirmCloseMonth}
        variant="default"
        icon="check"
        isLoading={closeMonthMutation.isPending}
      />

      <ConfirmDialog
        open={reopenMonthDialogOpen}
        onOpenChange={setReopenMonthDialogOpen}
        title={`Reabrir Mês (${monthNames[selectedMonth - 1]}/${selectedYear})`}
        description={
          <div className="space-y-4">
            <p>
              Os dados voltarão a ser <strong>calculados em tempo real</strong> e poderão mudar
              conforme alterações nos custos{profitAdvancesEnded ? '' : ' e antecipações'} atuais.
            </p>

            <p className="text-sm text-muted-foreground">
              O snapshot salvo será mantido, mas os dados exibidos serão recalculados em tempo real
              até que o mês seja fechado novamente.
            </p>
          </div>
        }
        confirmText="Reabrir Mês"
        cancelText="Cancelar"
        onConfirm={handleConfirmReopenMonth}
        variant="warning"
        icon="warning"
        isLoading={reopenMonthMutation.isPending}
      />
    </div>
  );
}
